import React from "react";
import { Pagination, Spinner, TooltipCustom } from "@services/ui-components";
import { useTranslation } from "react-i18next";
import { Button, Col } from "react-bootstrap";
import moment from "moment/moment";
import { TaggedRequestItem } from "./types";
import { PER_PAGE } from "./constants";
import StatusBadge from "./StatusBadge";

export type RequestsTableProps = {
    items: TaggedRequestItem[];
    loading: boolean;
    totalCount: number;
    currentPage: number;
    pageCount: string;
    onPageChange: (page: number) => void;
    onRowClick: (item: TaggedRequestItem) => void;
    tooltipResetKey: number;
};

const RequestsTable: React.FC<RequestsTableProps> = ({
    items,
    loading,
    totalCount,
    currentPage,
    pageCount,
    onPageChange,
    onRowClick,
    tooltipResetKey,
}) => {
    const { t } = useTranslation();

    const firstItemIndex = (currentPage - 1) * PER_PAGE + 1;
    const lastItemIndex = Math.min(currentPage * PER_PAGE, totalCount);

    return (
        <>
            <div className="table-responsive scrollbar mb-4">
                {loading ? (
                    <Spinner />
                ) : items.length ? (
                    <table className="table mt-6 mb-2 align-top">
                        <thead>
                            <tr className="fs-7">
                                <th className="col-2 ps-lg-4">{t("Request Date")}</th>
                                <th className="col-1">{t("Type")}</th>
                                <th className="col-2">{t("Venue")}</th>
                                <th className="col-2">{t("Contact person")}</th>
                                <th className="col-2">{t("Contacts")}</th>
                                <th className="col-2">{t("Status")}</th>
                                <th className="col-2">{t("Note")}</th>
                                <th className="col-1"></th>
                            </tr>
                        </thead>
                        <tbody className="fs-7">
                            {items.map((item, index) => (
                                <tr key={`${item.requestType}-${item.id ?? index}`}>
                                    <td className="py-3 ps-lg-4 text-nowrap">
                                        {moment(item.createdAt).format("D MMMM,")}
                                        <br />
                                        {moment(item.createdAt).format("HH:mm")}
                                    </td>
                                    <td className="py-3">
                                        <span className="d-flex align-items-center fs-7">
                                            <i
                                                className={`bi bi-circle-fill me-2 ${item.requestType === "add" ? "text-green" : "text-danger"}`}
                                                style={{ fontSize: 6 }}
                                            ></i>
                                            {item.requestType === "add" ? t("Addition") : t("Deletion")}
                                        </span>
                                    </td>
                                    <td className="py-3">
                                        <b>{item.venueName}</b>
                                    </td>
                                    <td className="py-3">
                                        {item.contactName} / {item.role}
                                    </td>
                                    <td className="py-3">
                                        {item.contactPhone}
                                        <br />
                                        {item.contactEmail}
                                    </td>
                                    <td className="py-3">
                                        <StatusBadge item={item} />
                                    </td>
                                    <td className="py-3">{item.internalNote ? item.internalNote : "–"}</td>
                                    <td className="py-3 text-lg-end pe-lg-4 px-0 px-lg-2">
                                        <TooltipCustom placement="bottom" text={t("Details")} key={tooltipResetKey}>
                                            <Button
                                                onClick={() => onRowClick(item)}
                                                variant="outline-secondary"
                                                className="btn-size outline-secondary text-dark p-0"
                                            >
                                                <i className="bi bi-eye"></i>
                                            </Button>
                                        </TooltipCustom>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <div className="w-100 justify-content-md-center">
                        <Col md={10} className="mt-5 pt-5 mx-auto">
                            <p className="fs-5 text-center font-weight-bold">
                                {t("No data found for the specified parameters.")}
                            </p>
                        </Col>
                    </div>
                )}
            </div>

            {items.length ? (
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <Pagination
                        t={t}
                        pages={parseInt(pageCount ?? "0")}
                        currentPage={currentPage}
                        onPageChange={onPageChange}
                        firstItemIndex={firstItemIndex}
                        lastItemIndex={lastItemIndex}
                        totalItems={totalCount}
                    />
                </div>
            ) : null}
        </>
    );
};

export default RequestsTable;
